<script>
import { defineComponent } from 'vue'
import Spinner from '../../../content/common/spinner.vue'
import LoadingPlaceholder from '../../../content/common/loadingPlaceholder.vue'
import SvgExclamation from '../../../content/common/svg-exclamation.vue'

export default defineComponent({
    name: 'locationPlan',
    components: { SvgExclamation, LoadingPlaceholder, Spinner },
    props: {
        locationId: Number
    },
    data() {
        return {

            loading: true,
            error: false,
            loadingForAWhile: false,
            loadingTimeoutHandle: null,

            plan: null,
            projectId: Laravel.activeProject,

            planImageLoaded: false,
            previewsLoaded: {},
            showSpinners: false,
            spinnersTimeoutHandle: null,

            t_error_loading: Lang.langGet('Locations could not be loaded.'),
            t_loading: Lang.langGet('Loading...'),
            t_plan: Lang.langGet('Plan for location')
        }
    },
    methods: {
        fetchPlan() {

            this.loading = true
            this.error = false

            axios.post('/show-plan', {
                location_id: this.locationId,
                project_id: this.projectId
            }).then((response) => {
                this.plan = response.data.data

                this.$nextTick(async () => {

                    this.previewsLoaded = {}
                    this.showSpinners = false
                    clearTimeout(this.spinnersTimeoutHandle)
                    this.spinnersTimeoutHandle = setTimeout(() => this.showSpinners = true, 800)

                    await this.$nextTick()

                    for (const client of this.plan.clients) {
                        const element = document.getElementById(`preview${client.id}`)
                        if (element.complete) {
                            if (element.naturalWidth > 0) {
                                this.onPreviewLoad(client.id)
                            } else {
                                this.onPreviewError(client.id)
                            }
                        }
                    }
                })
            }).catch((error) => {
                this.error = error
                this.plan = null
                if (error.response.status === 401) {
                    window.location = '/login'
                }

            }).finally(() => {
                if (this.planImageLoaded) {
                    this.loading = false
                }
            })
        },
        onPreviewLoad(id) {
            this.previewsLoaded = { ...this.previewsLoaded, [id]: true }
        },
        onPreviewError(id) {
            this.previewsLoaded = { ...this.previewsLoaded, [id]: false }
        },
        getPreviewStyle(shown) {
            if (!shown) {
                return { width: 0, height: 0 }
            }
            return {}
        },
        onPlanImageLoad() {
            this.loading = false
            this.planImageLoaded = true
        },
        onPlanImageError() {
            this.loading = false
            this.planImageLoaded = true
        }
    },
    watch: {
        loading(val) {
            this.$emit('loading', val)

            if (val) {
                this.loadingTimeoutHandle = setTimeout(() => this.loadingForAWhile = true, 1000)
            } else {
                clearTimeout(this.loadingTimeoutHandle)
                this.loadingForAWhile = false
            }
        },
        plan(val) {
            this.$emit('plan', val)
        },
        locationId: {
            immediate: true,
            handler(val) {
                if (val != null) this.fetchPlan()
            }
        }
    },
    mounted() {
        const element = this.$refs.planImage
        if (element?.complete) {
            if (element.naturalWidth > 0) {
                this.onPlanImageLoad()
            } else {
                this.onPlanImageError()
            }
        }
    },
    destroyed() {
        clearTimeout(this.spinnersTimeoutHandle)
        clearTimeout(this.loadingTimeoutHandle)
    }
})
</script>

<template>
    <div>
        <loading-placeholder v-if="planImageLoaded ? loadingForAWhile : loading"
                             :overlay="planImageLoaded" />
        <div v-if="error"
             class="ibox-content">
            <div class="load-locations-notifications">
                <div class="alert alert-danger"
                     style="margin-bottom: 0;">
                    {{ t_error_loading }}
                </div>
            </div>
        </div>
        <div v-if="plan"
             class="location-plan">
            <img v-if="plan.plan"
                 ref="planImage"
                 :alt="t_plan"
                 :src="`/storage/fields/${projectId}/${plan.plan}`"
                 @error="onPlanImageError"
                 @load="onPlanImageLoad" />
            <template v-for="client in plan.clients">
                <a :aria-label="`Link to location ${client.name}`" :href="client.detailurl">
                    <div :class="`status-${client.status}-bg`"
                         :data-balloon="client.name"
                         :style="{ left: `${client.x}%`, top: `${client.y}%` }"
                         class="preview-marker"
                         data-balloon-pos="up">
                        <spinner
                            v-if="showSpinners && (previewsLoaded[client.id] === undefined)" />
                        <transition name="fade">
                            <img v-if="previewsLoaded[client.id] !== false"
                                 :id="`preview${client.id}`"
                                 :ref="`preview${client.id}`"
                                 :alt="`Preview screenshot of ${client.name}`"
                                 :src="client.screenshot_url"
                                 :style="getPreviewStyle(previewsLoaded[client.id])"
                                 @error="onPreviewError(client.id)"
                                 @load="onPreviewLoad(client.id)">
                        </transition>
                        <transition name="fade">
                            <div v-if="previewsLoaded[client.id] === false">
                                <svg-exclamation />
                            </div>
                        </transition>
                    </div>
                </a>
            </template>
        </div>
    </div>
</template>
